import React from 'react';
import { Empty, Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { SegmentRow } from '@/types';
import { fmtNum, fmtUsd } from '../bq-insights/bqMath';
import { dailyPayerRate, sumOf, usd4 } from './monetizationMath';
import type { TypedEntry } from './monetizationTabState';
import { BackendDays } from './tableParts';
import { titleWithInfo, totalRow } from './tableHelpers';
import { TabBody } from './TabBody';

const { Text } = Typography;

export const USER_DAYS_INFO =
  'summed over the days in the range; a player active on 3 days counts 3 times';

const columns: ColumnsType<SegmentRow> = [
  { title: 'Spender tier', dataIndex: 'spenderTier' },
  {
    title: titleWithInfo('Player-days', USER_DAYS_INFO),
    dataIndex: 'playerDays',
    align: 'right',
    render: (v) => fmtNum(v),
  },
  {
    title: titleWithInfo('Payer-days', USER_DAYS_INFO),
    dataIndex: 'payerDays',
    align: 'right',
    render: (v) => fmtNum(v),
  },
  {
    title: titleWithInfo('Payer rate (daily)', 'Payer-days / player-days: the daily payer rate'),
    align: 'right',
    render: (_, r) => dailyPayerRate(r.payerDays, r.playerDays),
  },
  { title: 'Purchases', dataIndex: 'purchases', align: 'right', render: (v) => fmtNum(v) },
  {
    title: 'IAP revenue (gross)',
    dataIndex: 'iapRevenueUsd',
    align: 'right',
    render: (v) => fmtUsd(v),
  },
  { title: 'Ad revenue', dataIndex: 'adRevenueUsd', align: 'right', render: (v) => fmtUsd(v) },
  {
    title: 'Ad impressions',
    dataIndex: 'adImpressions',
    align: 'right',
    render: (v) => fmtNum(v),
  },
  {
    title: titleWithInfo(
      'ARPDAU (gross)',
      'Gross of refunds: (IAP + ads) / player-days. The Summary tile is net of refunds.'
    ),
    dataIndex: 'arpdauUsd',
    align: 'right',
    render: (v: number | null) => usd4(v),
  },
  {
    title: 'Backend coverage',
    render: (_, r) => <BackendDays backendDays={r.backendDays} days={r.days} />,
  },
];

const Content: React.FC<{ rows: SegmentRow[] }> = ({ rows }) => {
  if (rows.length === 0) {
    return (
      <Empty description="No segment data in this range" image={Empty.PRESENTED_IMAGE_SIMPLE} />
    );
  }
  return (
    <div>
      <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 8 }}>
        Player-days and payer-days are summed over the days in the range (a player active on 3 days
        counts 3 times), so they have no total row. Revenue, purchases and impressions do.
      </Text>
      <Table<SegmentRow>
        size="small"
        rowKey="spenderTier"
        columns={columns}
        dataSource={rows}
        pagination={false}
        scroll={{ x: 'max-content' }}
        summary={() =>
          totalRow([
            null,
            null, // player-days: no total
            null, // payer-days: no total
            null,
            fmtNum(sumOf(rows, (r) => r.purchases)),
            fmtUsd(sumOf(rows, (r) => r.iapRevenueUsd)),
            fmtUsd(sumOf(rows, (r) => r.adRevenueUsd)),
            fmtNum(sumOf(rows, (r) => r.adImpressions)),
            null,
            null,
          ])
        }
      />
    </div>
  );
};

interface Props {
  entry: TypedEntry<SegmentRow[]>;
  onRetry?: () => void;
}

/** DAU, payers and revenue per spender tier (the country filter is in the page header). */
export const SegmentsTab: React.FC<Props> = ({ entry, onRetry }) => (
  <TabBody entry={entry} onRetry={onRetry}>
    {(rows) => <Content rows={rows} />}
  </TabBody>
);
